import { useState } from "react";
import { toast } from "sonner";
import {
  Heart,
  Music2,
  Send,
  Mail as MailIcon,
  MessageSquare,
  MapPin,
  Radio,
  Church,
  ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import { extractApiError } from "../lib/errors";
import {
  CONTACT_EMAIL,
  CONTACT_WHATSAPP,
  whatsappHref,
  whatsappHrefFor,
  emailHref,
  CONTACT_PHONES,
  EMAILS_LIFETALK,
  EMAILS_FATHERS_HOUSE,
  RADIO_OFFICE_NAME,
  RADIO_OFFICE_ADDRESS,
  MAP_LINK_RADIO,
  CHURCH_NAME,
  CHURCH_ADDRESS,
  MAP_LINK_CHURCH,
} from "../lib/siteConfig";

const prayerInit = { name: "", contact_number: "", location: "", request_text: "" };
const songInit = { listener_name: "", city: "", song_title: "", dedicated_message: "" };
const contactInit = { name: "", email: "", phone: "", subject: "", message: "" };

function validatePrayer(v) {
  const e = {};
  if (!v.name.trim()) e.name = "Name is required.";
  if (!/^[+\d][\d\s\-()]{2,}$/.test(v.contact_number.trim()))
    e.contact_number = "Enter a valid contact number.";
  if (!v.location.trim()) e.location = "Location is required.";
  if (v.request_text.trim().length < 5) e.request_text = "Tell us how we can pray (5+ characters).";
  return e;
}

function validateSong(v) {
  const e = {};
  if (!v.listener_name.trim()) e.listener_name = "Your name is required.";
  if (!v.city.trim()) e.city = "City is required.";
  if (!v.song_title.trim()) e.song_title = "Tell us the artist or song.";
  return e;
}

function FieldError({ msg, id }) {
  if (!msg) return null;
  return (
    <p data-testid={id} className="mt-1.5 text-[11px] text-[#E31C3D] tracking-wide">
      {msg}
    </p>
  );
}

export default function Contact() {
  const [prayer, setPrayer] = useState(prayerInit);
  const [song, setSong] = useState(songInit);
  const [contactForm, setContactForm] = useState(contactInit);
  const [perr, setPerr] = useState({});
  const [serr, setSerr] = useState({});
  const [cerr, setCerr] = useState({});
  const [pLoading, setPLoading] = useState(false);
  const [sLoading, setSLoading] = useState(false);
  const [cLoading, setCLoading] = useState(false);

  const submitPrayer = async (ev) => {
    ev.preventDefault();
    const errs = validatePrayer(prayer);
    setPerr(errs);
    if (Object.keys(errs).length) return;
    try {
      setPLoading(true);
      await api.post("/prayer-requests", prayer);
      toast.success("Prayer request received. Our intercessors will lift you up.");
      setPrayer(prayerInit);
    } catch (e) {
      toast.error(extractApiError(e, "Could not submit prayer request."));
    } finally {
      setPLoading(false);
    }
  };

  const submitSong = async (ev) => {
    ev.preventDefault();
    const errs = validateSong(song);
    setSerr(errs);
    if (Object.keys(errs).length) return;
    try {
      setSLoading(true);
      await api.post("/song-requests", song);
      toast.success("Song request received. Tune in — we'll play it on air.");
      setSong(songInit);
    } catch (e) {
      toast.error(extractApiError(e, "Could not submit song request."));
    } finally {
      setSLoading(false);
    }
  };

  const submitContact = async (ev) => {
    ev.preventDefault();
    const errs = {};
    if (!contactForm.name.trim()) errs.name = "Name is required.";
    if (!/^\S+@\S+\.\S+$/.test(contactForm.email.trim()))
      errs.email = "Please enter a valid email.";
    if (contactForm.message.trim().length < 5)
      errs.message = "A short message helps us reply (5+ characters).";
    setCerr(errs);
    if (Object.keys(errs).length) return;
    try {
      setCLoading(true);
      await api.post("/contact-messages", contactForm);
      toast.success("Message received. We'll be in touch shortly.");
      setContactForm(contactInit);
    } catch (e) {
      toast.error(extractApiError(e, "Could not send message."));
    } finally {
      setCLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-canvas">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
          Contact Us
        </span>
        <h1 className="mt-4 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
          Send a prayer. Request a song. Reach out.
        </h1>
        <p className="mt-5 text-[#C5D1EB] max-w-2xl leading-relaxed">
          We read every line. Our intercessors pray over each request, and our
          studio team plays the songs you love over the live broadcast.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Prayer */}
          <form
            id="prayer"
            data-testid="prayer-form"
            onSubmit={submitPrayer}
            noValidate
            className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9"
          >
            <div className="flex items-center gap-3">
              <Heart size={20} className="text-[#C5001A]" />
              <h2 className="font-display text-2xl text-white">Prayer Request</h2>
            </div>
            <p className="mt-2 text-xs text-[#C5D1EB]/70">
              All requests are treated confidentially.
            </p>

            <div className="mt-7 space-y-5">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Name
                </label>
                <input
                  data-testid="prayer-name"
                  className="brand-input"
                  type="text"
                  value={prayer.name}
                  onChange={(e) => setPrayer({ ...prayer, name: e.target.value })}
                  placeholder="Your full name"
                />
                <FieldError id="prayer-name-error" msg={perr.name} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Contact Number
                </label>
                <input
                  data-testid="prayer-contact"
                  className="brand-input"
                  type="tel"
                  value={prayer.contact_number}
                  onChange={(e) => setPrayer({ ...prayer, contact_number: e.target.value })}
                  placeholder="+64 ..."
                />
                <FieldError id="prayer-contact-error" msg={perr.contact_number} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Location
                </label>
                <input
                  data-testid="prayer-location"
                  className="brand-input"
                  type="text"
                  value={prayer.location}
                  onChange={(e) => setPrayer({ ...prayer, location: e.target.value })}
                  placeholder="City, Country"
                />
                <FieldError id="prayer-location-error" msg={perr.location} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Prayer Request
                </label>
                <textarea
                  data-testid="prayer-text"
                  className="brand-input"
                  rows={5}
                  value={prayer.request_text}
                  onChange={(e) => setPrayer({ ...prayer, request_text: e.target.value })}
                  placeholder="How can we agree with you in prayer?"
                />
                <FieldError id="prayer-text-error" msg={perr.request_text} />
              </div>
              <button
                data-testid="prayer-submit"
                disabled={pLoading}
                className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors"
              >
                <Send size={15} /> {pLoading ? "Sending…" : "Send Prayer Request"}
              </button>
            </div>
          </form>

          {/* Song */}
          <form
            id="song"
            data-testid="song-form"
            onSubmit={submitSong}
            noValidate
            className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9"
          >
            <div className="flex items-center gap-3">
              <Music2 size={20} className="text-[#C5001A]" />
              <h2 className="font-display text-2xl text-white">Song Request</h2>
            </div>
            <p className="mt-2 text-xs text-[#C5D1EB]/70">
              Dedications air on the listener show, Fridays 8pm NZ time.
            </p>

            <div className="mt-7 space-y-5">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Listener Name
                </label>
                <input
                  data-testid="song-name"
                  className="brand-input"
                  type="text"
                  value={song.listener_name}
                  onChange={(e) => setSong({ ...song, listener_name: e.target.value })}
                  placeholder="Your name on air"
                />
                <FieldError id="song-name-error" msg={serr.listener_name} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  City
                </label>
                <input
                  data-testid="song-city"
                  className="brand-input"
                  type="text"
                  value={song.city}
                  onChange={(e) => setSong({ ...song, city: e.target.value })}
                  placeholder="e.g. Tauranga"
                />
                <FieldError id="song-city-error" msg={serr.city} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Artist / Song Title
                </label>
                <input
                  data-testid="song-title"
                  className="brand-input"
                  type="text"
                  value={song.song_title}
                  onChange={(e) => setSong({ ...song, song_title: e.target.value })}
                  placeholder="e.g. Hillsong · What A Beautiful Name"
                />
                <FieldError id="song-title-error" msg={serr.song_title} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Dedicated Message
                </label>
                <textarea
                  data-testid="song-message"
                  className="brand-input"
                  rows={5}
                  value={song.dedicated_message}
                  onChange={(e) => setSong({ ...song, dedicated_message: e.target.value })}
                  placeholder="A short message we can read on air."
                />
              </div>
              <button
                data-testid="song-submit"
                disabled={sLoading}
                className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors"
              >
                <Send size={15} /> {sLoading ? "Sending…" : "Send Song Request"}
              </button>
            </div>
          </form>
        </div>

        {/* Contact Us card with direct channels + general message form */}
        <div className="mt-6 grid lg:grid-cols-12 gap-6" id="contact">
          {/* Quick channels */}
          <aside className="lg:col-span-5 bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9 flex flex-col">
            <div className="flex items-center gap-3">
              <MessageSquare size={20} className="text-[#C5001A]" />
              <h2 className="font-display text-2xl text-white">Contact Us</h2>
            </div>
            <p className="mt-3 text-sm text-[#C5D1EB] leading-relaxed">
              For partnerships, broadcast enquiries, or anything else — reach
              the team directly.
            </p>

            <ul className="mt-7 space-y-5 text-sm" data-testid="contact-channels">
              <li>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#C5D1EB]/70 mb-2 flex items-center gap-2">
                  <Radio size={11} className="text-[#C5001A]" />
                  Life Talk Trust · email
                </p>
                <div className="space-y-1.5">
                  {EMAILS_LIFETALK.map((em) => (
                    <a
                      key={em}
                      href={`mailto:${em}`}
                      data-testid={`contact-email-lt-${em}`}
                      className="flex items-center gap-2.5 text-white hover:text-[#C5D1EB] break-all"
                    >
                      <MailIcon size={13} className="text-[#C5001A] shrink-0" />
                      {em}
                    </a>
                  ))}
                </div>
              </li>

              <li>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#C5D1EB]/70 mb-2 flex items-center gap-2">
                  <MailIcon size={11} className="text-[#C5001A]" />
                  Father's House Church · email
                </p>
                <div className="space-y-1.5">
                  {EMAILS_FATHERS_HOUSE.map((em) => (
                    <a
                      key={em}
                      href={`mailto:${em}`}
                      data-testid={`contact-email-fh-${em}`}
                      className="flex items-center gap-2.5 text-white hover:text-[#C5D1EB] break-all"
                    >
                      <MailIcon size={13} className="text-[#C5001A] shrink-0" />
                      {em}
                    </a>
                  ))}
                </div>
              </li>

              <li>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#C5D1EB]/70 mb-2 flex items-center gap-2">
                  <MessageSquare size={11} className="text-[#C5001A]" />
                  WhatsApp & Phone
                </p>
                <div className="space-y-1.5">
                  {CONTACT_PHONES.map((p) => (
                    <a
                      key={p}
                      href={whatsappHrefFor(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-phone-${p}`}
                      className="flex items-center gap-2.5 text-white hover:text-[#C5D1EB]"
                    >
                      <MessageSquare size={13} className="text-[#C5001A] shrink-0" />
                      {p}
                    </a>
                  ))}
                </div>
              </li>
            </ul>

            <div className="mt-8 pt-7 border-t border-white/10 grid gap-5">
              <AddressCard
                Icon={Radio}
                label={RADIO_OFFICE_NAME}
                address={RADIO_OFFICE_ADDRESS}
                mapLink={MAP_LINK_RADIO}
                testid="address-card-radio"
                buttonLabel="View on Google Maps"
              />
              <AddressCard
                Icon={Church}
                label={CHURCH_NAME}
                address={CHURCH_ADDRESS}
                mapLink={MAP_LINK_CHURCH}
                testid="address-card-church"
                buttonLabel="Get Directions"
              />
            </div>
          </aside>

          {/* General contact form */}
          <form
            id="contact-form"
            data-testid="contact-form"
            onSubmit={submitContact}
            noValidate
            className="lg:col-span-7 bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9"
          >
            <h3 className="font-display text-xl text-white tracking-tight">
              Send the team a message
            </h3>
            <p className="mt-2 text-xs text-[#C5D1EB]/70">
              We read every message and reply by email or WhatsApp.
            </p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Name
                </label>
                <input
                  data-testid="contact-name"
                  className="brand-input"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                />
                <FieldError id="contact-name-error" msg={cerr.name} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  data-testid="contact-email"
                  className="brand-input"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
                <FieldError id="contact-email-error" msg={cerr.email} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Phone (optional)
                </label>
                <input
                  data-testid="contact-phone"
                  className="brand-input"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Subject
                </label>
                <input
                  data-testid="contact-subject"
                  className="brand-input"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                Message
              </label>
              <textarea
                data-testid="contact-message"
                rows={5}
                className="brand-input"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              />
              <FieldError id="contact-message-error" msg={cerr.message} />
            </div>

            <button
              data-testid="contact-submit"
              disabled={cLoading}
              className="mt-6 inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors"
            >
              <Send size={15} /> {cLoading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}


/* -------------------------------------------------------------------------- */
/*  Reusable address + Google Maps card                                        */
/* -------------------------------------------------------------------------- */
function AddressCard({ Icon, label, address, mapLink, buttonLabel, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative bg-[#0B0C10]/60 border border-white/10 p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#C5001A]/15 border border-[#C5001A]/60 text-[#C5001A]">
          <Icon size={15} />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#C5D1EB]/70">
            {label}
          </p>
          <p className="mt-1.5 text-sm text-white leading-relaxed flex items-start gap-2">
            <MapPin size={14} className="mt-0.5 text-[#C5001A] shrink-0" />
            {address}
          </p>
        </div>
      </div>
      <a
        href={mapLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testid}-map-link`}
        className="group mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C5D1EB] hover:text-white border-b border-[#C5001A]/40 hover:border-[#C5001A] pb-0.5 transition-colors"
      >
        {buttonLabel}
        <ExternalLink
          size={12}
          className="opacity-70 group-hover:opacity-100 transition-opacity"
        />
      </a>
    </div>
  );
}
